import { FC, useState, useEffect, Fragment, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import templatesApi from "api/landlord/templates";
import { DeleteTemplateModal } from "../../../shared/app/VerificationTemplate/modals/DeleteTemplateModal";
import { CreateTemplateModal } from "../../../shared/app/VerificationTemplate/modals/CreateTemplateModal";
import {
  Template,
  VerificationSchema
} from "types/landlord/verificationTemplate";

import { getErrorMessage } from "utils/error";
import { useVerificationTemplatesController } from "../controller";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";

type VerificationMenuProps = {
  verificationTemplate: Template;
  addTemplate: (template: Template) => void;
  removeTemplateById: (id: string) => void;
  verificationSchemas: VerificationSchema[];
};

export const VerificationMenu: FC<VerificationMenuProps> = ({
  verificationTemplate,
  addTemplate,
  removeTemplateById,
  verificationSchemas
}) => {
  const [template, setTemplate] = useState<Template>(verificationTemplate);
  const [isModalOpen, setModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { refetchTemplates } = useVerificationTemplatesController();

  const navigate = useNavigate();

  const createTemplate = async () => {
    try {
      const data = await templatesApi.createTemplate(template);
      toast.success("New template successfully created");
      setModalOpen(false);
      addTemplate(data.verification_template);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      toast.error(
        errorMessage === "validation_failed"
          ? "Template name is already taken"
          : errorMessage
      );
    }
  };

  const handleTemplateUpdate = (updatedTemplate: Template) => {
    setTemplate(updatedTemplate);
  };

  const deleteTemplate = async () => {
    try {
      await templatesApi.deleteTemplate(template.id);
      toast.success("Template has been successfully deleted");
      removeTemplateById(template.id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleMakeTemplateDefault = async () => {
    try {
      await templatesApi.setDefaultTemplate(template.id);
      refetchTemplates();
      toast.success("Template has been successfully set as default");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleEditClick = (e: any) => {
    e.stopPropagation();
    navigate(`/verification-templates/${verificationTemplate.id}`);
  };

  const handleDuplicateClick = (e: any) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const handleDeleteClick = (e: any) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  useEffect(() => {
    setTemplate(verificationTemplate);
  }, [verificationTemplate]);

  const actions = useMemo(() => {
    const ret = [
      {
        title: "Edit",
        onClick: handleEditClick
      },
      {
        title: "Duplicate",
        onClick: handleDuplicateClick
      },
      {
        title: "Delete",
        onClick: handleDeleteClick
      }
    ];

    if (!template.default)
      ret.push({
        title: "Make default",
        onClick: handleMakeTemplateDefault
      });

    return ret;
  }, [template]);

  return (
    <Fragment>
      <ContextMenuPopover actions={actions} triggerClassName="translate-x-3" />
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {template && (
        <CreateTemplateModal
          opened={isModalOpen}
          template={template}
          handleCancel={() => setModalOpen(false)}
          handleSave={createTemplate}
          onTemplateUpdate={handleTemplateUpdate}
          verificationSchemas={verificationSchemas}
          defaultTemplate={verificationTemplate}
        />
      )}
      <DeleteTemplateModal
        opened={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        handleDelete={deleteTemplate}
      />
    </Fragment>
  );
};
